
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td, Button } from '../components/UI';
import { Building, MapPin, Package, Check, AlertCircle, AlertTriangle, Plus, RefreshCw, ShoppingCart } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { inventoryData } from '../data/inventory';
import { StockStatus, InventoryItem } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

// Reusing mock customer data for demo consistency
const mockCustomers = [
  { id: 'c1', name: 'VMI Enterprise AG', location: 'Zürich, CH' },
  { id: 'c2', name: 'Global Manufacturing Ltd', location: 'Geneva, CH' },
  { id: 'c3', name: 'Alpine Logistics Hub', location: 'Bern, CH' }
];

const SupplierCustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { user } = useAuth();
  const { addItem } = useCart();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const customer = mockCustomers.find(c => c.id === customerId);

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const customerInventory = useMemo(() => {
    if (!user || !user.companyName) return [];
    
    let items = [];
    if (customerId === 'c1') {
       items = inventoryData.filter(item => item.supplier === user.companyName);
    }
    
    return items.filter(item => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((item as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [user, customerId, columnFilters]);

  const stats = {
    total: customerInventory.length,
    critical: customerInventory.filter(i => i.status === StockStatus.CRITICAL).length,
    healthy: customerInventory.filter(i => i.status === StockStatus.HEALTHY).length
  };

  const getStatusBadge = (status: StockStatus) => {
    switch (status) {
      case StockStatus.HEALTHY:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30"><Check size={10} strokeWidth={4} /> {t('healthy')}</span>;
      case StockStatus.CRITICAL:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30"><AlertCircle size={10} strokeWidth={4} /> {t('critical')}</span>;
      case StockStatus.WARNING:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/30"><AlertTriangle size={10} strokeWidth={4} /> {t('warning')}</span>;
      default: return null;
    }
  };

  const handleQuickReplenish = (item: InventoryItem) => {
    const qty = Math.max(10, (item.minStock * 1.5) - item.currentStock); // Simple logic
    const price = 25.00; // Mock price
    addItem(item, Math.ceil(qty), price);
  };

  if (!customer) return <div>Customer not found</div>;

  return (
    <div className="p-6 space-y-8 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={customer.name}
        breadcrumbs={[{ label: t('customers') }, { label: customer.name }]}
        onBack={() => navigate('/supplier-customers')}
        actions={
           <div className="flex items-center gap-2 text-sm font-medium text-gray-500 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] px-4 py-2 rounded-sm">
              <MapPin size={16} /> {customer.location}
           </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <Card className="p-6 flex items-center justify-between border-l-4 border-l-brand-medium">
            <div>
               <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t('managed_skus')}</p>
               <h3 className="text-3xl font-black text-brand-dark dark:text-white">{stats.total}</h3>
            </div>
            <div className="w-10 h-10 bg-gray-100 dark:bg-[#262626] rounded-full flex items-center justify-center text-brand-medium">
               <Package size={20} />
            </div>
         </Card>
         <Card className="p-6 flex items-center justify-between border-l-4 border-l-red-500">
            <div>
               <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t('critical_alerts')}</p>
               <h3 className="text-3xl font-black text-red-600">{stats.critical}</h3>
            </div>
            <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-600">
               <AlertCircle size={20} />
            </div>
         </Card>
         <Card className="p-6 flex items-center justify-between border-l-4 border-l-green-500">
            <div>
               <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t('healthy_status')}</p>
               <h3 className="text-3xl font-black text-green-600">{stats.healthy}</h3>
            </div>
            <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center text-green-600">
               <Check size={20} strokeWidth={4} />
            </div>
         </Card>
      </div>

      <div className="space-y-4">
         <h3 className="text-lg font-bold text-brand-dark dark:text-white flex items-center gap-2">
            <Building size={18} /> {t('stock_at_customer_location')}
         </h3>
         
         <Card className="overflow-hidden border-t-4 border-t-brand-medium">
            <Table>
               <Thead>
                  <Tr isHeader>
                     <Th>
                        <div className="flex flex-col gap-2">
                           <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_number')}</span>
                           <input 
                              className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                              placeholder={t('filter_placeholder')}
                              onChange={(e) => handleColumnFilterChange('itemNumber', e.target.value)}
                           />
                        </div>
                     </Th>
                     <Th>
                        <div className="flex flex-col gap-2">
                           <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_name')}</span>
                           <input 
                              className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                              placeholder={t('filter_placeholder')}
                              onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                           />
                        </div>
                     </Th>
                     <Th className="text-right">
                        <div className="flex flex-col gap-2 items-end">
                           <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('current_stock')}</span>
                           <input 
                              className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                              placeholder={t('filter_placeholder')}
                              onChange={(e) => handleColumnFilterChange('currentStock', e.target.value)}
                           />
                        </div>
                     </Th>
                     <Th className="text-right">
                        <div className="flex flex-col gap-2 items-end">
                           <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('min_stock')}</span>
                           <input 
                              className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                              placeholder={t('filter_placeholder')}
                              onChange={(e) => handleColumnFilterChange('minStock', e.target.value)}
                           />
                        </div>
                     </Th>
                     <Th>
                        <div className="flex flex-col gap-2">
                           <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('status')}</span>
                           <select 
                              className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                              onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                           >
                              <option value="">{t('all')}</option>
                              <option value="Healthy">{t('healthy')}</option>
                              <option value="Critical">{t('critical')}</option>
                              <option value="Warning">{t('warning')}</option>
                           </select>
                        </div>
                     </Th>
                     <Th className="text-right">
                        {t('actions')}
                     </Th>
                  </Tr>
               </Thead>
               <Tbody>
                  {customerInventory.length > 0 ? customerInventory.map(item => (
                     <Tr key={item.id}>
                        <Td><span className="font-mono font-bold text-brand-dark dark:text-white">{item.itemNumber}</span></Td>
                        <Td className="text-[#161616] dark:text-white">{item.itemName}</Td>
                        <Td className="text-right font-bold text-[#161616] dark:text-white">{item.currentStock}</Td>
                        <Td className="text-right text-gray-500">{item.minStock}</Td>
                        <Td>{getStatusBadge(item.status)}</Td>
                        <Td className="text-right">
                           <Button size="sm" variant="outline" onClick={() => handleQuickReplenish(item)}>
                              <ShoppingCart size={14} className="mr-2" /> {t('propose_consignment')}
                           </Button>
                        </Td>
                     </Tr>
                  )) : (
                     <Tr>
                        <Td colSpan={6} className="text-center py-16">
                           <div className="flex flex-col items-center justify-center gap-4 text-gray-400">
                              <Package size={48} strokeWidth={1} />
                              <div className="text-xs font-bold uppercase tracking-widest">{t('no_stock_data')}</div>
                           </div>
                        </Td>
                     </Tr>
                  )}
               </Tbody>
            </Table>
         </Card>
      </div>
    </div>
  );
};

export default SupplierCustomerDetail;
